import { useEffect } from "react";

import Cookies from "js-cookie";

import { isClient } from "./dom.ts";
import { getLocalStorage, setLocalStorage } from "./localstorage.ts";

export type PageType = "SEO" | "PFM";
export type JourneyLocalStoragePersona = {
    team?:
        | "Marketing"
        | "Software Development"
        | "IT Support"
        | "Design"
        | "Operations";
    persona?: string;
};
export type JourneyLocalStorage = JourneyLocalStoragePersona & {
    anchor?: {
        name: string;
        type: PageType;
    };
};
export type BXPAnchorCookie = {
    anchor: string;
    type: PageType;
};
const BXP_ANCHOR_COOKIE_NAME = "bxp_gateway_anchor";
const GWP_LOCAL_STORAGE_NAME = "onboarding-journey";
const COOKIE_EXPIRY = 60 * 60 * 24 * 7; // 7 days

/**
 * Custom hook to create the local storage using bxp anchor cookie and also update the local storage with the persona and team,
 * the name of the local storage will be "onboarding-journey"
 * @param {JourneyLocalStoragePersona} value - persona object to update in the local storage.
 * @returns function to get the localstorage.
 * @returns function to update the localstorage, which accepts a value parameter.
 * @example Here are some example of the usage
 *
 *     ```
 *      useAnchorLocalStorage();
 *         // OR
 *      useAnchorLocalStorage({ persona: "Marketing", team: "Marketing" });
 *          // OR
 *       const handleLocalStorage = useAnchorLocalStorage();
 *       handleLocalStorage({ persona: "Marketing", team: "Marketing" });
 *      ```
 */
export function useAnchorLocalStorage(value?: JourneyLocalStoragePersona) {
    useEffect(() => {
        updateLocalStorage(value);
    }, [value]);

    return {
        getAnchorLocalStorage: () => {
            return isClient && getLocalStorage(GWP_LOCAL_STORAGE_NAME);
        },
        setAnchorLocalStorage: updateLocalStorage
    };

    function updateLocalStorage(value?: JourneyLocalStoragePersona) {
        if (isClient) {
            const bxpGatewayAnchorCookie = Cookies.get(BXP_ANCHOR_COOKIE_NAME);
            const journeyLocalStorage = getLocalStorage(GWP_LOCAL_STORAGE_NAME);
            if (bxpGatewayAnchorCookie || journeyLocalStorage) {
                try {
                    const bxpGatewayAnchorObj = JSON.parse(
                        bxpGatewayAnchorCookie || "null"
                    ) as BXPAnchorCookie;
                    const currentLocalStorageObj = JSON.parse(
                        journeyLocalStorage || "null"
                    ) as JourneyLocalStorage;
                    const anchorStorage: JourneyLocalStorage =
                        currentLocalStorageObj
                            ? { ...currentLocalStorageObj, ...value }
                            : {
                                  anchor: {
                                      name: bxpGatewayAnchorObj.anchor,
                                      type: bxpGatewayAnchorObj.type
                                  },
                                  ...value
                              };
                    setLocalStorage(
                        GWP_LOCAL_STORAGE_NAME,
                        JSON.stringify(anchorStorage),
                        COOKIE_EXPIRY
                    );
                } catch (err) {
                    console.error("Error creating local storage", err);
                }
            }
        }
    }
}
